<script setup>
import { onBeforeUnmount, onMounted, ref, useId, watch } from "vue";
import BootstrapModal from "bootstrap/js/dist/modal";
import ButtonApp from "@/components/common/other/ButtonApp.vue";

/**
 * A reusable modal component that provides a customizable dialog overlay.
 * Uses Bootstrap's modal classes for styling and functionality.
 * @example
 * <ModalApp v-model:is-open="showModal" title="My Modal">
 *   <template #body>
 *     Modal content goes here
 *   </template>
 *   <template #footer>
 *     <button>Send</button>
 *   </template>
 * </ModalApp>
 */

const props = defineProps({
  id: {
    type: String,
  },
  title: {
    type: String,
  },
  description: {
    type: String,
  },
  eyebrow: {
    type: String,
  },
  showFooter: {
    type: Boolean,
    default: true,
  },
  staticBackdrop: {
    type: Boolean,
    default: false,
  },
});

const isOpen = defineModel("isOpen", { type: Boolean, default: false });

const generatedId = useId();
const id = props.id ?? generatedId;

const modalRef = ref(null);
let modalInstance = null;
let isClosingByState = false;
let isVisible = false;
let lowerBackdrop = null;

function openModal() {
  modalInstance?.show();
}

function stackAboveOpenModals(element, level) {
  const backdrops = document.querySelectorAll(".modal-backdrop");
  const backdrop = backdrops[backdrops.length - 1];
  const zIndex = Number(getComputedStyle(element).zIndex) + level * 20;

  element.style.zIndex = zIndex;

  if (backdrop) {
    backdrop.style.zIndex = zIndex - 10;
  }

  lowerBackdrop = backdrops[backdrops.length - 2] ?? null;
  lowerBackdrop?.style.setProperty("opacity", "0");
}

function restoreLowerBackdrop() {
  lowerBackdrop?.style.removeProperty("opacity");
  lowerBackdrop = null;
}

function closeModal() {
  isClosingByState = true;
  modalInstance?.hide();
}

watch(isOpen, () => {
  if (isOpen.value) {
    openModal();
  } else {
    closeModal();
  }
});

onMounted(() => {
  const element = modalRef.value;

  modalInstance = new BootstrapModal(
    modalRef.value,
    props.staticBackdrop ? { backdrop: "static", keyboard: false } : {},
  );

  modalRef.value.addEventListener("show.bs.modal", () => {
    isVisible = true;

    const level = document.querySelectorAll(".modal.show").length;

    if (level) {
      queueMicrotask(() => stackAboveOpenModals(element, level));
    }
  });

  modalRef.value.addEventListener("hide.bs.modal", restoreLowerBackdrop);

  modalRef.value.addEventListener("shown.bs.modal", () => {
    isClosingByState = false;

    if (!isOpen.value) {
      closeModal();
    }
  });

  modalRef.value.addEventListener("hidden.bs.modal", () => {
    isVisible = false;
    element.style.removeProperty("z-index");

    const wasClosingByState = isClosingByState;
    isClosingByState = false;

    if (wasClosingByState && isOpen.value) {
      openModal();
      return;
    }

    isOpen.value = false;
  });

  if (isOpen.value) {
    openModal();
  }
});

onBeforeUnmount(() => {
  const instance = modalInstance;
  const element = modalRef.value;

  modalInstance = null;

  if (!instance) {
    return;
  }

  if (!isVisible) {
    instance.dispose();
    return;
  }

  element.addEventListener("hidden.bs.modal", () => instance.dispose(), {
    once: true,
  });

  element.addEventListener("shown.bs.modal", () => instance.hide(), {
    once: true,
  });

  instance.hide();
});
</script>

<template>
  <Teleport to="body">
    <div
      :id="id"
      ref="modalRef"
      aria-hidden="true"
      :aria-labelledby="id"
      class="modal fade"
      role="dialog"
      tabindex="-1"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div
            class="modal-header"
            :class="{
              'modal-header--bare': !title,
              'modal-header--described': description || eyebrow,
            }"
          >
            <div v-if="description || eyebrow" class="modal-heading">
              <span v-if="eyebrow" class="modal-eyebrow">{{ eyebrow }}</span>
              <h2 v-if="title" class="modal-title">
                {{ title }}
              </h2>
              <p v-if="description" class="modal-description">
                {{ description }}
              </p>
            </div>
            <h2 v-else-if="title" class="modal-title">
              {{ title }}
            </h2>

            <ButtonApp
              variant="tertiary"
              icon="close"
              icon-source="material"
              aria-label="Закрити"
              class="modal-close"
              @click="isOpen = false"
            />
          </div>

          <div class="modal-body">
            <slot name="body"></slot>
          </div>

          <div class="modal-footer" v-if="showFooter">
            <slot name="footer"></slot>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
.modal-content {
  border-radius: 12px;
}

.modal-header {
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid var(--gray-200);
}

.modal-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  line-height: 22px;
  color: var(--gray-950);
}

.modal-body {
  padding: 24px;
}

.modal-footer {
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid var(--gray-200);
  border-bottom-right-radius: 11px;
  border-bottom-left-radius: 11px;
  background: var(--gray-50);

  > * {
    margin: 0;
  }
}

.modal-header--described {
  align-items: flex-start;
}

.modal-header--bare {
  border-bottom: 0;
  padding-bottom: 0;
}

.modal-heading {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.modal-eyebrow {
  font-size: 14px;
  line-height: 22px;
  color: var(--gray-400);
}

.modal-description {
  margin: 0;
  font-size: 14px;
  line-height: 22px;
  color: var(--gray-600);
}

.modal-close {
  flex-shrink: 0;
  margin-left: auto;
}
</style>
